// src/security/v2/useV2CommunityRoles.ts
//
// DEV-971 — Derive each community's effective capabilities for the current
// user via the V2 evaluator. Backs the Lead → Communities and Explore →
// Communities consumer-side filters so V2-issued grants (via the
// Permissions panel) show up alongside legacy `community_role_assignments`
// rows during the shadow-mode transition window. After DEV-876 deprecates
// the legacy admin UI, the V1 read can be dropped and this becomes the
// single source.

import { useEffect, useState } from "react";
import { permissionsApiV2 } from "./apiClient";
import { evaluateCommunitySecurityV2 } from "./evaluator";
import type {
  CapabilityDto,
  CapabilityName,
  CommunityEvaluationInputs,
  GUID,
  MyGrantsResponse,
  PermissionSetDto,
} from "./types";

interface CommunityEntity {
  entity_guid: string;
}

interface CommunityRelationship {
  child_entity_guid: string;
  parent_entity_guid: string;
}

interface UseV2CommunityRolesArgs {
  userGuid: GUID;
  /** Set true when the user has TOTP enrolled. Controls the requires_2fa gate
   *  inside the evaluator — bundles flagged requires2fa contribute no caps
   *  when this is false. */
  is2FAEnabled?: boolean;
  entities?: CommunityEntity[];
  relationships?: CommunityRelationship[];
}

interface UseV2CommunityRolesResult {
  /** Map from upper-cased community entity_guid → effective capability set.
   *  Communities for which the evaluator returns zero capabilities are
   *  omitted from the map (consumers should check `.has(guid)` first). */
  capsByCommunity: Map<string, Set<CapabilityName>>;
  loading: boolean;
  error: string | null;
}

// Module-scope catalog cache — capabilities + bundles are stable across
// users and rarely change. Same convention as useShadowPermissions.
let _catalog: {
  capabilities: CapabilityDto[];
  permissionSets: PermissionSetDto[];
} | null = null;

async function loadCatalog(): Promise<typeof _catalog> {
  if (_catalog) return _catalog;
  const [capabilities, permissionSets] = await Promise.all([
    permissionsApiV2.capabilities(),
    permissionsApiV2.permissionSets(),
  ]);
  _catalog = { capabilities, permissionSets };
  return _catalog;
}

function buildParentMap(relationships: CommunityRelationship[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const r of relationships || []) {
    const child = String(r.child_entity_guid || "").toUpperCase();
    const parent = String(r.parent_entity_guid || "").toUpperCase();
    if (child && parent) m.set(child, parent);
  }
  return m;
}

/** Walk parent chain from a community to its root. Cycle-safe. */
function ancestorsOf(communityGuid: string, parentMap: Map<string, string>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  let cur = parentMap.get(communityGuid.toUpperCase());
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    out.push(cur);
    cur = parentMap.get(cur);
  }
  return out;
}

/**
 * Compute each community's effective capabilities for the current user via
 * the V2 community evaluator.
 *
 * **Sysadmin behavior:** `isSystemAdmin` is forced to `false` regardless of
 * the caller's actual sysadmin status. The two consumer pages (Lead →
 * Communities, Explore → Communities) want "communities I explicitly
 * lead / can touch via a grant," not "communities I CAN admin via sysadmin
 * bypass." Without this override, sysadmins would see every community in
 * the system in their leadership list, which is incorrect — sysadmin
 * authority is platform-wide power, not community leadership.
 *
 * **Cascade is honored:** Org-admin grants on Rotary International cascade
 * down to every club via `*_descendants` capabilities. The evaluator's
 * Rules 1.5 + 6 walk the ancestor chain; we supply that chain via
 * `ancestorCommunityGuidsForCommunity` for each target community.
 *
 * **Cost:** O(entities × ancestor-depth × grants) per render trigger.
 * Cached catalog at module scope; grants refetched per call. For Skip's
 * working dataset (~50-200 communities) this runs sub-millisecond.
 */
export function useV2CommunityRoles({
  userGuid,
  is2FAEnabled = false,
  entities,
  relationships,
}: UseV2CommunityRolesArgs): UseV2CommunityRolesResult {
  const [state, setState] = useState<UseV2CommunityRolesResult>({
    capsByCommunity: new Map(),
    loading: true,
    error: null,
  });

  // Stable dep keys — using array references would re-fire on every parent
  // render. Length + a hash-equivalent (joined GUIDs) lets us detect actual
  // changes without forcing reference stability on the caller.
  const entitiesKey = (entities || []).map((e) => e.entity_guid).join(",");
  const relsKey = (relationships || []).length;

  useEffect(() => {
    let cancelled = false;

    if (!userGuid) {
      setState({ capsByCommunity: new Map(), loading: false, error: null });
      return;
    }

    (async () => {
      try {
        const catalog = await loadCatalog();
        if (!catalog || cancelled) return;

        let myGrants: MyGrantsResponse;
        try {
          myGrants = await permissionsApiV2.myGrants();
        } catch {
          // Network or auth failure → no grants. Empty map; caller falls
          // through to V1 read.
          myGrants = {
            userGuid,
            activeSlotGuids: [],
            directGrants: [],
            slotGrants: [],
          };
        }
        if (cancelled) return;
        // DEV-1014 fix shipped — the evaluator now normalizes GUID
        // comparisons internally, so the boundary normalization that
        // lived here is no longer needed.

        const parentMap = buildParentMap(relationships || []);
        const result = new Map<string, Set<CapabilityName>>();

        for (const ent of entities || []) {
          const guid = String(ent.entity_guid || "").toUpperCase();
          if (!guid) continue;

          const inputs: CommunityEvaluationInputs = {
            userGuid,
            isSystemAdmin: false, // intentional — see jsdoc above
            is2FAEnabled,
            capabilities: catalog.capabilities,
            permissionSets: catalog.permissionSets,
            myGrants,
            ancestorCommunityGuidsForCommunity: ancestorsOf(guid, parentMap),
          };

          const evalRes = evaluateCommunitySecurityV2(guid, inputs);
          if (evalRes.capabilities.size > 0) {
            result.set(guid, evalRes.capabilities);
          }
        }

        if (!cancelled) {
          setState({ capsByCommunity: result, loading: false, error: null });
        }
      } catch (err) {
        if (!cancelled) {
          setState({
            capsByCommunity: new Map(),
            loading: false,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGuid, is2FAEnabled, entitiesKey, relsKey]);

  return state;
}
